import { FormEvent, useEffect, useMemo, useState } from "react";
import type { Session } from "./auth";
import { seedInitialCatalog, saveProduct, saveSiteContent, uploadProductImage } from "./cms";
import type { Product, SiteContent } from "./domain";

type CmsSection = "products" | "content";

function slugify(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function emptyProduct(sortOrder: number): Product {
  return {
    id: "",
    name: "",
    description: "",
    variants: ["Mini isi 5"],
    unit: "box",
    demoPrices: { customer: 0, reseller: 0, partner: 0 },
    active: true,
    featured: false,
    stock: 0,
    sortOrder,
  };
}

export function AdminCms({ products, content, session }: { products: Product[]; content: SiteContent; session: Session | null }) {
  const [section, setSection] = useState<CmsSection>("products");
  const [selectedId, setSelectedId] = useState(products[0]?.id ?? "new");
  const selected = useMemo(() => products.find((item) => item.id === selectedId), [products, selectedId]);
  const [draft, setDraft] = useState<Product>(() => selected ?? emptyProduct(products.length + 1));
  const [copyDraft, setCopyDraft] = useState(content);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [message, setMessage] = useState("");
  const canEditContent = session?.role === "owner";

  function updateVariantPrice(variant: string, audience: "customer" | "reseller" | "partner", value: number) {
    const existing = draft.variantPrices?.[variant] ?? draft.demoPrices;
    setDraft({ ...draft, variantPrices: { ...draft.variantPrices, [variant]: { ...existing, [audience]: value } } });
  }

  useEffect(() => { setDraft(selected ?? emptyProduct(products.length + 1)); setImageFile(null); }, [selected, products.length]);
  useEffect(() => setCopyDraft(content), [content]);

  async function handleProduct(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const id = draft.id || slugify(draft.name);
    if (!id) { setMessage("Nama produk wajib diisi."); return; }
    setMessage("Menyimpan produk...");
    try {
      let imageUrl = draft.imageUrl;
      if (imageFile) imageUrl = await uploadProductImage(id, imageFile);
      await saveProduct({ ...draft, id, imageUrl });
      setSelectedId(id);
      setMessage("Produk berhasil disimpan dan katalog akan diperbarui otomatis.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Produk gagal disimpan.");
    }
  }

  async function handleContent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("Menyimpan teks website...");
    try {
      await saveSiteContent(copyDraft);
      setMessage("Teks website berhasil diperbarui.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Teks gagal disimpan.");
    }
  }

  async function handleSeed() {
    setMessage("Memasukkan katalog awal...");
    try { await seedInitialCatalog(); setMessage("Tujuh produk dan teks awal berhasil dibuat."); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Seed data gagal."); }
  }

  return (
    <section className="cms-panel">
      <div className="cms-tabs" role="tablist" aria-label="Pengelolaan website">
        <button className={section === "products" ? "active" : ""} onClick={() => setSection("products")}>Produk & harga</button>
        <button className={section === "content" ? "active" : ""} onClick={() => setSection("content")}>Teks website</button>
      </div>
      {message && <p className="inline-message" aria-live="polite">{message}</p>}

      {section === "products" && <div className="cms-layout">
        <aside className="cms-list">
          <button className={selectedId === "new" ? "selected" : ""} onClick={() => setSelectedId("new")}>＋ Tambah produk</button>
          {products.map((product) => <button key={product.id} className={selectedId === product.id ? "selected" : ""} onClick={() => setSelectedId(product.id)}><span>{product.name}</span><small>{product.active === false ? "Nonaktif" : `Stok ${product.stock ?? 0}`}</small></button>)}
          {products.length === 0 && <button className="seed-button" onClick={() => void handleSeed()}>Masukkan 7 produk awal</button>}
        </aside>
        <form className="cms-form" onSubmit={handleProduct}>
          <div className="cms-form-heading"><div><p className="eyebrow">Editor katalog</p><h2>{draft.id ? "Ubah produk" : "Produk baru"}</h2></div><label className="toggle"><input type="checkbox" checked={draft.active !== false} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /> Tampil</label></div>
          <div className="form-grid">
            <label>Nama produk<input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required /></label>
            <label>ID produk<input value={draft.id} onChange={(event) => setDraft({ ...draft, id: slugify(event.target.value) })} placeholder="otomatis dari nama" /></label>
            <label className="full">Deskripsi<textarea value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} required /></label>
            <label className="full">Varian, satu per baris<textarea value={draft.variants.join("\n")} onChange={(event) => setDraft({ ...draft, variants: event.target.value.split("\n") })} required /></label>
            <div className="variant-price-editor full">
              <strong>Harga per varian</strong>
              {draft.variants.map((rawVariant, index) => {
                const variant = rawVariant.trim();
                if (!variant) return null;
                const prices = draft.variantPrices?.[variant] ?? draft.demoPrices;
                return <fieldset key={`${variant}-${index}`}><legend>{variant}</legend>
                  <label>Konsumen<input type="number" min="0" value={prices.customer} onChange={(event) => updateVariantPrice(variant, "customer", Number(event.target.value))} required /></label>
                  <label>Reseller<input type="number" min="0" value={prices.reseller} onChange={(event) => updateVariantPrice(variant, "reseller", Number(event.target.value))} required /></label>
                  <label>Mitra B2B<input type="number" min="0" value={prices.partner} onChange={(event) => updateVariantPrice(variant, "partner", Number(event.target.value))} required /></label>
                </fieldset>;
              })}
            </div>
            <label>Stok<input type="number" min="0" value={draft.stock ?? 0} onChange={(event) => setDraft({ ...draft, stock: Number(event.target.value) })} /></label>
            <label>Urutan<input type="number" min="0" value={draft.sortOrder ?? 0} onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) })} /></label>
            <label>Satuan<input value={draft.unit} onChange={(event) => setDraft({ ...draft, unit: event.target.value })} /></label>
            <label className="full">Foto produk asli<input type="file" accept="image/*" onChange={(event) => setImageFile(event.target.files?.[0] ?? null)} /></label>
          </div>
          {(imageFile || draft.imageUrl) && <div className="cms-image-preview">{imageFile ? <img src={URL.createObjectURL(imageFile)} alt="Pratinjau upload asli" /> : <img src={draft.imageUrl} alt={draft.name} />}<span>File dipakai apa adanya tanpa modifikasi generatif.</span></div>}
          <button className="save-button" type="submit">Simpan produk</button>
        </form>
      </div>}

      {section === "content" && <form className="cms-form content-form" onSubmit={handleContent}>
        <div className="cms-form-heading"><div><p className="eyebrow">Editor konten</p><h2>Teks utama website</h2></div><span className="role-badge">Khusus owner</span></div>
        <div className="form-grid">
          {(Object.keys(copyDraft) as Array<keyof SiteContent>).map((key) => <label className={key.includes("Title") || key.includes("Description") ? "full" : ""} key={key}>{key.replace(/([A-Z])/g, " $1")}<textarea value={copyDraft[key]} onChange={(event) => setCopyDraft({ ...copyDraft, [key]: event.target.value })} disabled={!canEditContent} /></label>)}
        </div>
        <button className="save-button" type="submit" disabled={!canEditContent}>{canEditContent ? "Publikasikan perubahan teks" : "Login owner diperlukan"}</button>
      </form>}
    </section>
  );
}
